import type { BucketDefinition } from './useJobFilterBuckets'
import {
  type BucketDateRangeBoundary,
  type BucketDateRangeFilterDefinition,
  type BucketDateRangeLimits,
  type BucketDateRangePreset,
  PRESET_FUTURE_ANY,
  type ResolvedBucketDateRange,
} from './bucket-date-presets'
import {
  addCalendarDays,
  compareDates,
  diffCalendarDays,
  endOfLocalDay,
  isAfterDate,
  isBeforeDate,
  isSameCalendarDay,
  startOfLocalDay,
} from '@/utils/date-helpers'

/**
 * Converts a value to a Date at start of day
 */
export function toStartOfDayDate(value: Date): Date {
  return startOfLocalDay(value)
}

/**
 * Resolves a boundary definition to a specific date
 */
export function resolveBoundaryDate(boundary: BucketDateRangeBoundary, reference: Date): Date {
  if (boundary.kind === 'Relative') {
    return addCalendarDays(startOfLocalDay(reference), boundary.offsetInDays)
  }
  return toStartOfDayDate(boundary.date)
}

/**
 * Resolves a preset to a concrete date range
 */
export function resolvePresetToDateRange(
  preset: BucketDateRangePreset,
  referenceDate: Date = new Date(),
): ResolvedBucketDateRange | null {
  if (!preset.range) return null

  const reference = startOfLocalDay(referenceDate)
  const startMoment = resolveBoundaryDate(preset.range.start, reference)
  const endMoment = resolveBoundaryDate(preset.range.end, reference)

  return [startOfLocalDay(startMoment), endOfLocalDay(endMoment)]
}

export function normalizeDateRange(range: ResolvedBucketDateRange): ResolvedBucketDateRange {
  const [first, second] = range
  return compareDates(first, second) <= 0 ? [first, second] : [second, first]
}

export function rangesMatchByDay(
  rangeA: ResolvedBucketDateRange,
  rangeB: ResolvedBucketDateRange,
): boolean {
  return isSameCalendarDay(rangeA[0], rangeB[0]) && isSameCalendarDay(rangeA[1], rangeB[1])
}

export function clampDateRangeToMaxSpan(
  range: ResolvedBucketDateRange,
  maxSpanDays?: number | null,
): ResolvedBucketDateRange {
  const normalized = normalizeDateRange(range)
  if (maxSpanDays == null) return normalized

  const [start, end] = normalized
  const startMoment = toStartOfDayDate(start)
  const endMoment = toStartOfDayDate(end)
  const spanDays = diffCalendarDays(endMoment, startMoment) + 1

  if (spanDays <= maxSpanDays) {
    return normalized
  }

  const clampedEnd = endOfLocalDay(addCalendarDays(startMoment, maxSpanDays - 1))
  return [startMoment, clampedEnd]
}

export function clampDateRangeToAbsoluteLimits(
  range: ResolvedBucketDateRange,
  limits?: BucketDateRangeLimits,
): ResolvedBucketDateRange {
  if (!limits) return normalizeDateRange(range)

  const normalized = normalizeDateRange(range)
  let [startMoment, endMoment] = normalized.map((date) => toStartOfDayDate(date))

  if (limits.minDate) {
    const minMoment = toStartOfDayDate(limits.minDate)
    if (isBeforeDate(startMoment, minMoment)) startMoment = minMoment
    if (isBeforeDate(endMoment, minMoment)) endMoment = minMoment
  }

  if (limits.maxDate) {
    const maxMoment = toStartOfDayDate(limits.maxDate)
    if (isAfterDate(endMoment, maxMoment)) endMoment = maxMoment
    if (isAfterDate(startMoment, maxMoment)) startMoment = maxMoment
  }

  return normalizeDateRange([startMoment, endMoment])
}

export function clampDateRangeToSpanLimits(
  range: ResolvedBucketDateRange,
  limits?: BucketDateRangeLimits,
): ResolvedBucketDateRange {
  if (!limits) return normalizeDateRange(range)

  const normalized = normalizeDateRange(range)
  let [startMoment, endMoment] = normalized.map((date) => toStartOfDayDate(date))

  const spanDays = diffCalendarDays(endMoment, startMoment) + 1
  const { minSpanDays, maxSpanDays } = limits

  const spanExceedsMax = maxSpanDays != null && spanDays > maxSpanDays
  if (spanExceedsMax) {
    endMoment = addCalendarDays(startMoment, maxSpanDays - 1)
  }

  const adjustedSpan = diffCalendarDays(endMoment, startMoment)
  const spanBelowMin = minSpanDays != null && adjustedSpan < minSpanDays
  const canExpandToMin = maxSpanDays == null || (minSpanDays != null && minSpanDays <= maxSpanDays)

  if (spanBelowMin && canExpandToMin) {
    let targetEnd = addCalendarDays(startMoment, minSpanDays)

    if (limits.maxDate) {
      const maxMoment = toStartOfDayDate(limits.maxDate)
      const targetExceedsMaxDate = isAfterDate(targetEnd, maxMoment)
      if (targetExceedsMaxDate) {
        targetEnd = maxMoment
      }
    }

    endMoment = targetEnd
  }

  return normalizeDateRange([startMoment, endMoment])
}

export function applyBucketDateRangeLimits(
  range: ResolvedBucketDateRange,
  limits?: BucketDateRangeLimits,
): ResolvedBucketDateRange {
  if (!limits) {
    return normalizeDateRange(range)
  }

  const withAbsoluteBounds = clampDateRangeToAbsoluteLimits(range, limits)
  return clampDateRangeToSpanLimits(withAbsoluteBounds, limits)
}

export function normalizeDateRangeForFilter(
  range: ResolvedBucketDateRange,
  config: BucketDateRangeFilterDefinition,
): ResolvedBucketDateRange {
  const start = startOfLocalDay(range[0])
  const end = startOfLocalDay(range[1])
  const limited = applyBucketDateRangeLimits([start, end], config.limits)
  const normalizedStart = startOfLocalDay(limited[0])
  const normalizedEnd = endOfLocalDay(limited[1])
  return [normalizedStart, normalizedEnd]
}

/**
 * Gets the default date range for a bucket based on its preset configuration.
 * Returns null if the bucket doesn't have a default preset defined.
 */
export function getDefaultDateRangeForBucket(
  bucket: BucketDefinition,
): ResolvedBucketDateRange | null {
  const config = bucket.dateRangeFilter
  if (!config?.defaultPresetKey) return null

  const preset = config.presets?.find((p) => p.key === config.defaultPresetKey)
  if (!preset) return null

  return resolvePresetToDateRange(preset)
}

/**
 * Resolves a preset key to a concrete date range using the bucket's configuration.
 * Returns null if the preset is not found.
 */
export function getDateRangeForPreset(
  presetKey: string,
  config: BucketDateRangeFilterDefinition,
  referenceDate: Date = new Date(),
): ResolvedBucketDateRange | null {
  const preset = config.presets?.find((item) => item.key === presetKey)
  if (!preset) return null
  return resolvePresetToDateRange(preset, referenceDate)
}

/**
 * Returns the selected preset label, or a formatted date range string if no
 * preset is selected.
 */
export function getDateRangeFilterLabel(
  value: [Date, Date] | null,
  presets: BucketDateRangeFilterDefinition['presets'],
  presetKey?: string | null,
): string {
  if (presetKey === PRESET_FUTURE_ANY.key) return PRESET_FUTURE_ANY.label
  if (value === null) return ''

  if (presetKey) {
    return (
      presets?.find((item) => item.key === presetKey)?.label ?? formatDateRange(value[0], value[1])
    )
  }

  return formatDateRange(value[0], value[1])
}

export function getDateRangeTriggerLabel(
  value: [Date, Date] | null,
  presets: BucketDateRangeFilterDefinition['presets'],
  presetKey?: string | null,
): string {
  if (presetKey) {
    const activePreset = presets?.find((item) => item.key === presetKey)
    if (activePreset) {
      return activePreset.triggerLabel ?? activePreset.label
    }
  }

  if (!value) {
    return 'Custom date range'
  }

  return formatDateRange(value[0], value[1])
}

export function formatDateRange(startDate: Date, endDate?: Date): string {
  const formatter = new Intl.DateTimeFormat('en-US', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  })

  if (!endDate) {
    return formatter.format(startDate)
  }

  if (typeof formatter.formatRange === 'function') {
    // formatRange uses narrow no-break spaces (U+202F) around the en-dash, which
    // prevents CSS word-wrap from breaking at the separator. Replace with NBSP
    // before the dash (keep visually non-breaking) and a zero-width space after
    // (allow break opportunity) so long cross-year ranges can wrap after the dash.
    return formatter
      .formatRange(startDate, endDate)
      .replaceAll(/[ \u00A0\u202F]/gu, '\u00A0') // all spaces \u2192 NBSP (no break within each date)
      .replace(/\u00A0([\u2013-])\u00A0/u, '\u00A0$1\u200B\u00A0') // ZWS after dash = only break point; restore NBSP space
  }

  const isSameDay =
    startDate.getFullYear() === endDate.getFullYear() &&
    startDate.getMonth() === endDate.getMonth() &&
    startDate.getDate() === endDate.getDate()

  if (isSameDay) {
    return formatter.format(endDate)
  }

  const start = startDate.toLocaleDateString('en-US', {
    day: 'numeric',
    month: 'short',
    year: startDate.getFullYear() === endDate.getFullYear() ? undefined : 'numeric',
  })
  const end = formatter.format(endDate)

  return `${start} – ${end}`
}
